import type { GridPosition } from "../../shared/game";
import { SITE_TUNING } from "./siteTuning";
import type { RelicCourseBounds } from "../../shared/relicRunCourse";

type RelicCameraViewport = {
    width: number;
    height: number;
    cellSize: number;
    corner?: "top-left" | "bottom-left";
    bounds?: RelicCourseBounds;
    placement?: { x: number; y: number };
};

export const startRelicCamera = (runner: GridPosition, viewport: RelicCameraViewport): GridPosition => {
    const inset = SITE_TUNING.relicRun.camera.startInsetCells;
    const halfX = viewport.width / viewport.cellSize / 2;
    const halfY = viewport.height / viewport.cellSize / 2;
    const fallback = { x: runner.x + Math.max(0, halfX - inset), y: runner.y + Math.max(0, halfY - inset) * (viewport.corner === "bottom-left" ? -1 : 1) };
    if (!viewport.bounds) return fallback;
    const bounds = viewport.bounds;
    const placement = viewport.placement ?? { x: 0.35, y: 0.5 };
    const placeAxis = (min: number, max: number, half: number, seed: number, overflow: number) => {
        const padding = SITE_TUNING.relicRun.camera.courseInsetCells;
        const low = max - half + padding, high = min + half - padding;
        return low <= high ? low + (high - low) * (Number.isFinite(seed) ? Math.max(0, Math.min(1, seed)) : 0.5) : overflow;
    };
    return {
        x: placeAxis(bounds.minX, bounds.maxX, halfX, placement.x, fallback.x),
        y: placeAxis(bounds.minY, bounds.maxY, halfY, placement.y, fallback.y)
    };
};

/** Hold the room still until the runner reaches an edge, then follow at that
 * edge instead of recentering. The same rule works for turns and portrait. */
export const followRelicRunner = (
    camera: GridPosition,
    runner: GridPosition,
    viewport: RelicCameraViewport
): GridPosition => {
    const tuning = SITE_TUNING.relicRun.camera;
    const followAxis = (center: number, position: number, size: number, leftMargin: number = tuning.edgeMarginCells, rightMargin: number = tuning.edgeMarginCells) => {
        const radius = Math.max(tuning.minDeadzoneCells, size / viewport.cellSize / 2 - rightMargin);
        const leftRadius = Math.max(tuning.minDeadzoneCells, size / viewport.cellSize / 2 - leftMargin);
        return Math.max(position - radius, Math.min(position + leftRadius, center));
    };
    const fitAxis = (center: number, min: number | undefined, max: number | undefined, size: number): number | null => {
        if (min === undefined || max === undefined) return null;
        const half = size / viewport.cellSize / 2;
        const low = max - half + tuning.courseInsetCells;
        const high = min + half - tuning.courseInsetCells;
        // A resize can shrink the legal range around a previously fixed course.
        return low <= high ? Math.max(low, Math.min(high, center)) : null;
    };
    return {
        x: fitAxis(camera.x, viewport.bounds?.minX, viewport.bounds?.maxX, viewport.width) ??
            followAxis(camera.x, runner.x, viewport.width, tuning.leftEdgeMarginCells),
        y: fitAxis(camera.y, viewport.bounds?.minY, viewport.bounds?.maxY, viewport.height) ??
            followAxis(camera.y, runner.y, viewport.height,
            viewport.corner === "bottom-left" ? tuning.edgeMarginCells : tuning.leftEdgeMarginCells,
            viewport.corner === "bottom-left" ? tuning.leftEdgeMarginCells : tuning.edgeMarginCells)
    };
};
